import { unsubscribeFromPush } from "../lib/push";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError, apiRequest } from "../lib/api";
import { clearSecurityTokens } from "../lib/backendContract";
import { configureDateTime } from "../lib/format";
import { customerSignedOut, finishCustomerSsoRedirect, rememberCustomerSignOut, requestCustomerSso, saveCustomerSsoRedirect } from "../lib/customerSso";
import type {
  CustomerSession,
  RuntimeConfig,
  SessionKind,
  SessionUser,
  StaffSession,
} from "../lib/types";

type SessionStatus = "loading" | "authenticated" | "anonymous" | "error";

interface LoginResult {
  requiresMfa?: boolean;
  methods?: string[];
  user?: StaffSession;
  mfaSetupRequired?: boolean;
  passwordChangeRequired?: boolean;
}

export interface StaffMfaSetupResult {
  secret: string;
  otpauthUri: string;
  expiresAt: string;
}

export interface StaffMfaConfirmResult {
  recoveryCodes: string[];
}

interface SessionContextValue {
  status: SessionStatus;
  kind: SessionKind;
  user: SessionUser | null;
  config: RuntimeConfig;
  error: string | null;
  customerLoginPending: boolean;
  refresh: (options?: { silentSso?: boolean }) => Promise<void>;
  updateTimezone: (timezone: string) => Promise<void>;
  startCustomerLogin: () => void;
  loginStaff: (input: {
    email: string;
    password: string;
    mfaCode?: string;
    recoveryCode?: string;
  }) => Promise<LoginResult>;
  beginStaffMfaSetup: () => Promise<StaffMfaSetupResult>;
  confirmStaffMfaSetup: (code: string) => Promise<StaffMfaConfirmResult>;
  changeStaffPassword: (currentPassword: string, newPassword: string) => Promise<StaffSession>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function currentKind(): SessionKind {
  return window.location.pathname.startsWith("/agent") ? "staff" : "customer";
}

function normalizeCustomer(value: unknown): CustomerSession {
  const source = ((value as { user?: unknown })?.user ?? value) as Record<string, unknown>;
  return {
    kind: "customer",
    id: String(source.id ?? source.customerId ?? ""),
    externalId: source.externalId ? String(source.externalId) : undefined,
    name: String(source.name ?? source.displayName ?? "Next-Cart customer"),
    email: String(source.email ?? ""),
    avatarUrl: source.avatarUrl ? String(source.avatarUrl) : undefined,
    locale: source.locale ? String(source.locale) : undefined,
    timezone: source.timezone ? String(source.timezone) : undefined,
  };
}

function normalizeStaff(value: unknown): StaffSession {
  const envelope = (value ?? {}) as Record<string, unknown>;
  const source = (envelope.user ?? value) as Record<string, unknown>;
  const mfaVerifiedAt = envelope.mfaVerifiedAt ?? source.mfaVerifiedAt;
  return {
    kind: "staff",
    id: String(source.id ?? source.staffId ?? ""),
    name: String(source.name ?? source.displayName ?? "Support agent"),
    email: String(source.email ?? ""),
    avatarUrl: source.avatarUrl ? String(source.avatarUrl) : undefined,
    locale: source.locale ? String(source.locale) : undefined,
    timezone: source.timezone ? String(source.timezone) : undefined,
    roles: Array.isArray(source.roles) ? source.roles.map(String) : [],
    permissions: Array.isArray(source.permissions) ? source.permissions.map(String) : [],
    teamIds: Array.isArray(source.teamIds) ? source.teamIds.map(String) : undefined,
    mfaSetupRequired: Boolean(envelope.mfaSetupRequired ?? source.mfaSetupRequired ?? source.requiresMfaEnrollment),
    mfaVerifiedAt: typeof mfaVerifiedAt === "string" ? mfaVerifiedAt : null,
    passwordChangeRequired: Boolean(envelope.passwordChangeRequired ?? source.passwordChangeRequired),
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const kind = currentKind();
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  configureDateTime(user?.locale, user?.timezone, kind === "staff" ? "Asia/Ho_Chi_Minh" : "UTC");
  const [config, setConfig] = useState<RuntimeConfig>({});
  const [error, setError] = useState<string | null>(null);
  const configRef = useRef<RuntimeConfig>({});
  const inFlight = useRef<Promise<void> | null>(null);
  const [customerLoginPending, setCustomerLoginPending] = useState(false);
  const customerLoginInFlight = useRef(false);
  const signingOut = useRef(false);
  const explicitlySignedOut = useRef(customerSignedOut());

  const loadConfig = useCallback(async () => {
    if (configRef.current.dashSso || configRef.current.appName) return configRef.current;
    try {
      const next = await apiRequest<RuntimeConfig>("/config");
      configRef.current = next ?? {};
      setConfig(next ?? {});
      return next ?? {};
    } catch {
      return configRef.current;
    }
  }, []);

  const silentCustomerSso = useCallback(async (runtime: RuntimeConfig) => {
    if (explicitlySignedOut.current || !runtime.dashSso?.authorizeUrl || !runtime.dashSso.clientId) return null;
    const result = await requestCustomerSso(runtime, window.location.origin);
    return result.kind === "authenticated" ? normalizeCustomer(result.session) : null;
  }, []);

  const refresh = useCallback(
    async (options: { silentSso?: boolean } = {}) => {
      if (signingOut.current || customerLoginInFlight.current) return;
      if (inFlight.current) return inFlight.current;
      const operation = (async () => {
        setError(null);
        if (!user) setStatus("loading");
        const runtime = await loadConfig();
        if (kind === "customer" && window.location.pathname === "/auth/sso/callback") {
          try {
            const callbackUrl = window.location.href;
            // Do not retain a one-use authorization code in history/referrers.
            window.history.replaceState(window.history.state, "", window.location.pathname);
            const result = await finishCustomerSsoRedirect(callbackUrl);
            explicitlySignedOut.current = false;
            rememberCustomerSignOut(false);
            setUser(normalizeCustomer(result.session));
            setStatus("authenticated");
            window.location.replace(result.returnTo);
          } catch (callbackError) {
            setError(callbackError instanceof Error ? callbackError.message : "Sign-in failed. Please try again.");
            setUser(null);
            setStatus("anonymous");
          }
          return;
        }
        try {
          const session = await apiRequest<unknown>(
            kind === "staff" ? "/auth/staff/session" : "/auth/customer/session?revalidate=true",
          );
          const normalized = kind === "staff" ? normalizeStaff(session) : normalizeCustomer(session);
          setUser(normalized);
          setStatus("authenticated");
          return;
        } catch (requestError) {
          if (!(requestError instanceof ApiError) || requestError.status !== 401) {
            setError(requestError instanceof Error ? requestError.message : "Session check failed.");
            setStatus(user ? "authenticated" : "error");
            return;
          }
        }

        if (kind === "customer" && options.silentSso !== false) {
          try {
            const customer = await silentCustomerSso(runtime);
            if (customer) {
              setUser(customer);
              setStatus("authenticated");
              return;
            }
          } catch (ssoError) {
            setError(ssoError instanceof Error ? ssoError.message : "Single sign-on failed.");
          }
        }
        setUser(null);
        setStatus("anonymous");
      })().finally(() => {
        inFlight.current = null;
      });
      inFlight.current = operation;
      return operation;
    }, [kind, loadConfig, silentCustomerSso, user],
  );

  useEffect(() => {
    void refresh();
  }, []); // Session kind is fixed for the lifetime of a full route entry.

  useEffect(() => {
    const unauthorized = () => void refresh({ silentSso: kind === "customer" });
    const focus = () => {
      if (document.visibilityState === "visible" && status === "authenticated") void refresh({ silentSso: false });
    };
    window.addEventListener("support:unauthorized", unauthorized);
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    return () => {
      window.removeEventListener("support:unauthorized", unauthorized);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, [kind, refresh, status]);

  const updateTimezone = useCallback(async (timezone: string) => {
    const endpoint = kind === "staff" ? "/auth/staff/preferences" : "/auth/customer/preferences";
    const response = await apiRequest<{ timezone: string }>(endpoint, {
      method: "PATCH",
      body: { timezone },
    });
    setUser((current) => current ? { ...current, timezone: response.timezone } : current);
    configureDateTime(user?.locale, response.timezone, kind === "staff" ? "Asia/Ho_Chi_Minh" : "UTC");
  }, [kind, user?.locale]);

  const startCustomerLogin = useCallback(async () => {
    if (kind !== "customer" || customerLoginInFlight.current || signingOut.current) return;
    customerLoginInFlight.current = true;
    setCustomerLoginPending(true);
    setError(null);
    try {
      // Serialize against a passive session check so it cannot overwrite this login.
      await inFlight.current;
      setError(null);
      const result = await requestCustomerSso(await loadConfig(), window.location.origin);
      if (result.kind === "authenticated") {
        explicitlySignedOut.current = false;
        rememberCustomerSignOut(false);
        setUser(normalizeCustomer(result.session));
        setStatus("authenticated");
        if (window.location.pathname === "/auth/sso/callback") window.location.replace("/");
      } else {
        saveCustomerSsoRedirect(result.intent, window.location.href);
        // This explicit sign-in continues through the verified PKCE callback.
        explicitlySignedOut.current = false;
        rememberCustomerSignOut(false);
        window.location.assign(result.authorizeUrl);
      }
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Sign-in failed. Please try again.");
    } finally {
      customerLoginInFlight.current = false;
      setCustomerLoginPending(false);
    }
  }, [kind, loadConfig]);

  const loginStaff = useCallback(
    async (input: {
      email: string;
      password: string;
      mfaCode?: string;
      recoveryCode?: string;
    }) => {
      const result = await apiRequest<LoginResult | StaffSession>("/auth/staff/login", {
        method: "POST",
        body: input,
      });
      if ("requiresMfa" in result && result.requiresMfa) return result;
      const staff = normalizeStaff(result);
      setUser(staff);
      setStatus("authenticated");
      return {
        user: staff,
        mfaSetupRequired: staff.mfaSetupRequired,
        passwordChangeRequired: staff.passwordChangeRequired,
      };
    },
    [],
  );

  const beginStaffMfaSetup = useCallback(async () => (
    apiRequest<StaffMfaSetupResult>("/auth/staff/mfa/setup", { method: "POST", body: {} })
  ), []);

  const confirmStaffMfaSetup = useCallback(async (code: string) => {
    const result = await apiRequest<StaffMfaConfirmResult & Record<string, unknown>>("/auth/staff/mfa/confirm", {
      method: "POST",
      body: { code },
    });
    const staff = normalizeStaff(result);
    setUser(staff);
    setStatus("authenticated");
    return { recoveryCodes: Array.isArray(result.recoveryCodes) ? result.recoveryCodes.map(String) : [] };
  }, []);

  const changeStaffPassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const result = await apiRequest<Record<string, unknown>>("/auth/staff/password", {
      method: "POST",
      body: { currentPassword, newPassword },
    });
    const staff = normalizeStaff(result);
    setUser(staff);
    setStatus("authenticated");
    return staff;
  }, []);

  const logout = useCallback(async () => {
    if (signingOut.current) return;
    signingOut.current = true;
    try {
      // Finish a pending SSO exchange before revoking its newly issued session.
      await inFlight.current;
      await unsubscribeFromPush().catch(() => undefined);
      try {
        await apiRequest(kind === "staff" ? "/auth/staff/session" : "/auth/customer/session", {
          method: "DELETE",
        });
      } catch (logoutError) {
        if (kind === "customer" && !(logoutError instanceof ApiError && logoutError.status === 401)) {
          setError("Could not sign out of Support. Please try again.");
          throw logoutError;
        }
      }
      if (kind === "customer") {
        explicitlySignedOut.current = true;
        rememberCustomerSignOut(true);
      }
      clearSecurityTokens();
      setError(null);
      setUser(null);
      setStatus("anonymous");
    } finally {
      signingOut.current = false;
    }
  }, [kind]);

  const value = useMemo(
    () => ({
      status,
      kind,
      user,
      config,
      error,
      customerLoginPending,
      refresh,
      updateTimezone,
      startCustomerLogin,
      loginStaff,
      beginStaffMfaSetup,
      confirmStaffMfaSetup,
      changeStaffPassword,
      logout,
    }),
    [
      status,
      kind,
      user,
      config,
      error,
      customerLoginPending,
      refresh,
      updateTimezone,
      startCustomerLogin,
      loginStaff,
      beginStaffMfaSetup,
      confirmStaffMfaSetup,
      changeStaffPassword,
      logout,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}
